import { AppStoreBase } from './AppStoreBase';
import { IAppComponents } from '../models/IAppComponents';
import { action, computed, observable } from 'mobx';
import { StoreContext } from '../../../configuration/StoreContext';
import { Manifest } from '../../../configuration/Manifest';
import { IApplicationEmbedInput } from '@kurtosys/ksys-app-components/dist/components/base/ApplicationEmbed/models';
import { IInputs } from '../../../models/app/IInputs';
import { ISearchToggleButtonConfiguration } from '../models/ISearchToggleButtonConfiguration';
import { IButtonProps } from '@kurtosys/ksys-app-components/dist/components/base/Button/models';
import { IModalProps } from '@kurtosys/ksys-app-components/dist/components/base/Modal/models';
import { utils } from '@kurtosys/ksys-app-template';
import { IResultGroup, IResultItem } from '../models/IResultItem';
import { TSearchDocumentsResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/documents/TSearchDocumentsResponseBody';
import { TGlobalSearchResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/websites/TGlobalSearchResponseBody';
import { TSearchEntities, IDocumentSearchClause, IQueryOptions, IRedirect, IDocument } from '../../../models/commonTypes';
import { IDocumentSource, IDxmSource, IEntitySource, IInternalOptions } from '../../Results/models';
import { getExtension } from 'mime';
import { RedirectHelper } from '@kurtosys/ksys-app-template/dist/helpers';

/* [Component: appStoreComponentImport] */

export class AppStore extends AppStoreBase {

	@observable.ref dialogGuid: string;
	@observable alertMessage: string = '';
	@observable groupedResults: IResultGroup[] | undefined = undefined;

	static DEFAULT_LIMIT = 5;

	constructor(element: HTMLElement, url: string, storeContext: StoreContext, manifest: Manifest) {
		super(element, url, storeContext, manifest);
		this.dialogGuid = utils.guid.generateGuid();
	}

	@computed
	get dialogPlaceholderId() {
		return `ksys-gs-dialog-placeholder-${ this.dialogGuid }`;
	}

	@computed
	get hasData(): boolean {
		return true; // basic input will always show, not reliant on data to show.
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	}

	@action
	addAlertMessage = (message: string) => {
		this.alertMessage = message;
	};

	@computed
	get inputsForAppEmbed(): IApplicationEmbedInput[] {
		const inputs: Partial<IInputs> = this.appParamsHelper.inputs || {};
		return (Object.keys(inputs) as (keyof IInputs)[]).map((key) => {
			const value = inputs[key] as any;
			return {
				key,
				value,
			};
		});
	}

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get toggleButtonConfiguration(): ISearchToggleButtonConfiguration | undefined {
		return this.componentConfiguration && this.componentConfiguration.toggleButton;
	}

	@computed
	get internalOptions(): IInternalOptions | undefined {
		return this.componentConfiguration?.results?.internalOptions;
	}

	/**
	 *
	 * @default false
	 * @readonly
	 */
	@computed
	get toggleButtonEnabled(): boolean {
		const defaultValue = false;
		if (this.toggleButtonConfiguration && this.toggleButtonConfiguration.enabled !== undefined) {
			return this.toggleButtonConfiguration.enabled;
		}
		return defaultValue;
	}

	@computed
	get openButtonProps(): IButtonProps | undefined {
		if (this.toggleButtonConfiguration && this.toggleButtonEnabled) {
			const { text, icon, showText } = this.toggleButtonConfiguration;
			return {
				text: showText ? text : undefined,
				iconProps: icon,
			}
		}
	};

	@computed
	get modalProps(): IModalProps {
		const { searchBarStore } = this.storeContext;
		if (this.toggleButtonEnabled) {
			return {
				modalId: this.dialogPlaceholderId,
				title: this.appCardProps?.title || 'Search',
				footerContent: this.appCardProps?.footerContent,
				openButtonProps: this.openButtonProps,
				onClose: this.onModalClose,
				focusElement: searchBarStore.ref,
			};
		}
		return {
			title: 'Search',
		};
	}

	@action
	onModalClose = () => {
		const { searchBarStore } = this.storeContext;
		searchBarStore.searchTerm = undefined;
		searchBarStore.showMinimumCharacterError = false;
		this.groupedResults = undefined;
	}

	async customInitializeAfter() {
		this.storeContext.searchBarStore.initialize();
	}

	getHref = (redirect?: IRedirect, payload?: any): string => {
		if (redirect) {
			const payloadWithSearchTerm = {
				...payload || {},
				searchTerm: this.storeContext.searchBarStore.searchTerm,
			}
			const redirectHelper: RedirectHelper = new RedirectHelper(redirect.defaultUrl, redirect.base);
			return redirectHelper.getRedirectPath(redirect, payloadWithSearchTerm);
		}
		return '';
	}

	getDocumentHref = (document: IDocument, filenameQuery?: IQueryOptions) => {
		const { serviceUrl, token } = this.storeContext.kurtosysApiStore;
		const defaultFilenameQuery: IQueryOptions = {
			queryOptionsType: 'document',
			key: 'title',
		};
		const filename = this.getQueryValue(filenameQuery || defaultFilenameQuery, document, { context: { document } });
		const extension = getExtension(document.fileType);
		const fullFileName = extension ? `${ filename }.${ extension }` : filename;
		return `${ serviceUrl }/documents/retrieveDocument/?token=${ token }&clientCode=${ document.clientCode }&filename=${ fullFileName }`
	}

	orchestrateGroupedResults = (entityResults: IResultItem[], documentResults: IResultItem[], dxmResults: IResultItem[]) => {
		const groups: IResultGroup[] = [];
		if (this.internalOptions) {
			const { entities, documents, dxmContent } = this.internalOptions;
			if (entities && entityResults && entityResults.length > 0) {
				const href = this.getHref(entities.redirect);
				groups.push({
					label: entities.label || 'Products',
					items: entityResults || [],
					link: entities.redirect && {
						label: entities.redirectLabel || 'See All',
						href,
						onClick: () => this.onClick(href),
					},
				});
			}
			if (documents && documentResults && documentResults.length > 0) {
				const href = this.getHref(documents.redirect);
				groups.push({
					label: documents.label || 'Documents',
					items: documentResults || [],
					link: documents.redirect && {
						label: documents.redirectLabel || 'See All',
						href,
						onClick: () => this.onClick(href),
					},
				});
			}
			if (dxmContent && dxmResults && dxmResults.length > 0) {
				const href = this.getHref(dxmContent.redirect);
				groups.push({
					label: dxmContent.label || 'Articles',
					items: dxmResults || [],
					link: dxmContent.redirect && {
						label: dxmContent.redirectLabel || 'See All',
						href,
						onClick: () => this.onClick(href),
					},
				});
			}
		}
		this.groupedResults = groups;
	}

	@action
	fetchResults = async (searchTerm: string) => {
		const { translationStore, resultsStore, searchBarStore } = this.storeContext;
		const currentSearchTerm = searchBarStore.searchTerm;
		// v4 migration: Use a translation key for this and use the existing phrase as the default static text
		this.addAlertMessage('Fetching search results');
		const entityResults: IResultItem[] = [];
		const documentResults: IResultItem[] = [];
		const dxmResults: IResultItem[] = [];

		if (this.internalOptions) {
			const promises: Promise<void>[] = [];
			const { entities, documents, dxmContent } = this.internalOptions;
			if (entities) {
				const getEntities = async (entitySource: IEntitySource): Promise<void> => {
					entityResults.push(...await this.fetchEntityResults(entitySource, searchTerm));
				}
				promises.push(...entities.sources.map((e) => getEntities(e)));
			}
			if (documents) {
				const getDocuments = async (documentSource: IDocumentSource): Promise<void> => {
					documentResults.push(...await this.fetchDocumentResults(documentSource, searchTerm));
				}
				promises.push(...documents.sources.map((d) => getDocuments(d)));
			}
			if (dxmContent) {
				const getDxmContent = async (dxmSource: IDxmSource): Promise<void> => {
					dxmResults.push(...await this.fetchDxmResults(dxmSource, searchTerm));
				}
				promises.push(...dxmContent.sources.map((d) => getDxmContent(d)));
			}
			await Promise.all(promises);
		}
		if (currentSearchTerm === searchBarStore.searchTerm) {
			this.orchestrateGroupedResults(entityResults, documentResults, dxmResults);
			const total = entityResults.length + documentResults.length + documentResults.length;
			if (total > 0) {
				const totalGroups = this.groupedResults?.length;
				// v4 migration: Use a translation key for this and use the existing phrase as the default static text
				const finishedMessage = translationStore.translate('Finished loading search results. Found {total} results across {totalGroups} groups.', { total, totalGroups });
				this.addAlertMessage(finishedMessage);
			}
			else {
				this.addAlertMessage(resultsStore.noResultsPlaceholder);
			}
		}
	}

	@action
	fetchEntityResults = async (source: IEntitySource, searchTerm: string): Promise<IResultItem[]> => {
		const { entityType, properties, requestBody, label, url } = source;

		const search: IDocumentSearchClause[] = [...(requestBody?.search || [])];
		properties.forEach(property => {
			search.push({
				property,
				values: [searchTerm],
				matchtype: 'LIKE',
			});
		});

		const response = await this.storeContext.kurtosysApiStore.client.entity.search.callApi({
			body: {
				...requestBody,
				search,
				type: entityType,
				limit: requestBody?.limit || AppStore.DEFAULT_LIMIT,
			}
		});

		if (response.status >= 200 && response.status < 400) {
			const entities: TSearchEntities = await response.json();
			const resultItems: IResultItem[] = entities.values.map(entity => {
				const href = this.getHref(url, entity);
				return {
					type: 'redirect',
					link: {
						label: this.getQueryValue(label, entity, {
							context: {
								entityByType: {
									[entity.type]: entity
								}
							}
						}),
						href,
						onClick: () => { this.onClick(href) }
					}
				}
			});
			return resultItems;
		}
		else {
			this.log('error', {
				message: 'Problem fetching entity results',
				detail: await response.text(),
			})
		}
		return [];
	}

	formatFileSize = (bytes: number, decimalPoint = 2): string => {
		if (bytes == 0) return '0 Bytes';
		const k = 1000;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(decimalPoint)) + ' ' + sizes[i];
	};

	@action
	fetchDocumentResults = async (source: IDocumentSource, searchTerm: string): Promise<IResultItem[]> => {
		const { entityType, properties, requestBody, label, url, filename: filenameQuery } = source;

		const search: IDocumentSearchClause[] = [...(requestBody?.search || [])];
		if (properties.documentProperties) {
			properties.documentProperties.forEach(property => {
				search.push({
					property,
					values: [searchTerm],
					matchtype: 'LIKE',
					meta: false,
				});
			});
		}
		if (properties.meta) {
			properties.meta.forEach(property => {
				search.push({
					property,
					values: [searchTerm],
					matchtype: 'LIKE',
					meta: true,
				});
			});
		}

		const response = await this.storeContext.kurtosysApiStore.client.documents.search.callApi({
			body: {
				...requestBody,
				search,
				type: entityType,
				limit: requestBody?.limit || AppStore.DEFAULT_LIMIT,
			}
		});

		if (response.status >= 200 && response.status < 400) {
			const documents: TSearchDocumentsResponseBody = await response.json();
			const resultItems: IResultItem[] = documents.values.map(document => {
				const type = url ? 'redirect' : 'document';
				const labelValue = this.getQueryValue(label, document, {
					context: {
						document,
					}
				});
				const href = url ? this.getHref(url, document) : this.getDocumentHref(document, filenameQuery);
				const extension = getExtension(document.fileType);
				const documentFileSize: string =
					document.fileSize !== 0
						? this.formatFileSize(document.fileSize || 0)
						: 'File size unknown';
				const documentFileType: string = extension?.toUpperCase() || 'File type unknown';

				return {
					type,
					link: {
						label: labelValue,
						href,
						onClick: () => { this.onClick(href || '') },
						ariaLabel: `Download ${ labelValue }, type: ${ documentFileType }, size: ${ documentFileSize }`,
					}
				}
			});
			return resultItems;
		}
		else {
			this.log('error', {
				message: 'Problem fetching document results',
				detail: await response.text(),
			})
		}
		return [];
	}

	@action
	fetchDxmResults = async (source: IDxmSource, searchTerm: string): Promise<IResultItem[]> => {
		const { type, requestBody, label, url } = source;

		if (this.storeContext.kurtosysApiStore.client.websites?.globalSearch) {
			const response = await this.storeContext.kurtosysApiStore.client.websites?.globalSearch.callApi({
				body: {
					...requestBody,
					postType: type,
					searchTerm,
					paging: requestBody.paging || {
						limit: AppStore.DEFAULT_LIMIT,
					},
				}
			});

			if (response.status >= 200 && response.status < 400) {
				const dxmContent: TGlobalSearchResponseBody = await response.json();
				const resultItems: IResultItem[] = (dxmContent.articles || []).map(article => {
					const href = url ? this.getHref(url, article) : article.postPermalink;
					return {
						type: 'redirect' as any,
						link: {

							label: this.getQueryValue(label, article, {}),
							href,
							onClick: () => { this.onClick(href || '') }
						}
					}
				});
				return resultItems;
			}
			else {
				this.log('error', {
					message: 'Problem fetching DXM results',
					detail: await response.text(),
				})
			}
		}
		return [];
	}

	onClick = (href: string) => {
		if (this.analyticsHelper) {
			this.analyticsHelper.logEvent({
				event: 'redirect',
				eventType: 'click',
				context: { event: 'redirect', url: href },
			});
		}
	}


	@action
	clearResults = (mustAnnounce: boolean = true) => {
		if (!utils.typeChecks.isNullOrUndefined(this.groupedResults)) {
			this.groupedResults = undefined;
			if (mustAnnounce) {
				this.addAlertMessage('Search results have been cleared.');
			}
		}
	}
}